// Imports
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { useAppDispatch } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import APIForm, { APIFormOptions } from '../../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetGeneralResponse } from '../../../../../../../shareables/types/api/main/website/member/settings/general/get';
import { PutGeneralParameters } from '../../../../../../../shareables/types/api/main/website/member/settings/general/put';
import { GeneratedThemeProps } from '../../../../../../templates/template-style';
import { GetRegistrationResponse } from '../../../../../../../shareables/types/api/main/website/settings/registration/get';
import commonActions from '../../../../../../../shareables/foundation/front-end/redux/actions';
import { useOutletContext } from 'react-router-dom';
import RegistrationFields from '../../shared-components/registration-fields';


// Styled components
const Container = styled.div<{ theme: GeneratedThemeProps }>`
	line-height: 1.3125rem;
`;


// Define the accepted props
interface Props {
	storybookGeneralSettings?: GetGeneralResponse;
	storybookRegistrationSettings?: GetRegistrationResponse;
}


// Function component
const General: React.FC<Props> = ({ storybookGeneralSettings, storybookRegistrationSettings }) => {
	// Use state
	const [generalSettings, setGeneralSettings] = useState<GetGeneralResponse | null>(storybookGeneralSettings ?? null);
	const [registrationSettings, setRegistrationSettings] = useState<GetRegistrationResponse | null>(
		storybookRegistrationSettings ?? null
	);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use ref
	const ref = useRef<HTMLDivElement | null>(null);
	
	
	// Use React Router functionality
	const updateHeight: (newHeight: number) => void = useOutletContext();
	
	
	// Update height for the members settings page
	useEffect(() => {
		if (!(storybookGeneralSettings || storybookRegistrationSettings) && ref.current) {
			updateHeight(ref.current.clientHeight);
		}
	}, [updateHeight, generalSettings, registrationSettings, storybookGeneralSettings, storybookRegistrationSettings]);
	
	
	// Load general settings and registration settings on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetGeneralResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/member/settings/general`,
				completion: (json) => {
					setGeneralSettings(json.response);
				},
			})
		);
		
		void dispatch(
			apiActions.call<undefined, undefined, GetRegistrationResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/settings/registration`,
				completion: (json) => {
					setRegistrationSettings(json.response);
				},
			})
		);
	}, [dispatch]);
	
	
	// If loading, then display nothing
	if (generalSettings === null || registrationSettings === null) {
		return null;
	}
	
	
	// Build form options
	const formOptions: APIFormOptions<PutGeneralParameters, undefined, undefined> = {
		action: 'PUT',
		uri: `/website/${window.location.hostname}/member/settings/general`,
		submitButton: {
			text: 'Save',
		},
		recoverState: false,
	};
	
	
	// Handle form completion
	const handleFormCompletion = () => {
		dispatch(
			commonActions.showPageMessage({
				color: 'success',
				title: 'Success',
				message: 'These settings have been saved.',
			})
		);
	};
	
	
	// Return JSX
	return (
		<Container ref={ref}>
			<APIForm options={formOptions} onCompletion={handleFormCompletion}>
				<RegistrationFields generalSettings={generalSettings} askGender={registrationSettings.askGender} />
			</APIForm>
		</Container>
	);
};

export default General;
